import { Outlet } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuth } from "../context/AuthContext";
import { getContextAwareToken } from "../utils/storageHelper";
import RefreshIcon from "../assets/icon/RefreshIcon";
import { StatsCard } from "./shared/statscard";
import NoshowIcon from "../assets/icon/NoshowIcon";
import PercentageIcon from "../assets/icon/PercentageIcon";
import MedicIcon from "../assets/icon/MedicIcon";
import NoPatientIcon from "../assets/icon/NoPatientIcon";
import {
  CircularProgress,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";

interface NoShowStats {
  totalAppointments: number;
  noShowCount: number;
  rebookedCount: number;
  recoveryRate: string;
}

export const NoShowLayout = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<NoShowStats>({
    totalAppointments: 0,
    noShowCount: 0,
    rebookedCount: 0,
    recoveryRate: "0%",
  });
  const [loading, setLoading] = useState(true);

  const fetchNoShowStats = useCallback(async () => {
    setLoading(true);
    try {
      const token = getContextAwareToken(user?.token, user?.role) || "";

      if (!token) {
        console.error("No token found");
        toast.error("Authentication token not found");
        setLoading(false);
        return;
      }

      const BASE_URL = "https://vivotal-backend.vercel.app/api/v1";

      // Fetch no-show list
      const noShowResponse = await axios.get(`${BASE_URL}/no-show/list`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const noShowData = noShowResponse.data?.data || [];
      console.log("No-show data:", noShowData);

      // Deduplicate by appointment_id - same logic as NoShowTable
      // The backend creates 2 records per cancelled appointment (for 30s and 60s follow-up messages)
      // But we only want to count 1 entry per appointment in the stats
      const uniqueNoShows = noShowData.reduce((acc: any[], record: any) => {
        const appointmentId = record.appointment_id;
        const existingRecord = acc.find(
          (r: any) => r.appointment_id === appointmentId
        );

        if (!existingRecord) {
          acc.push(record);
        }

        return acc;
      }, []);

      console.log(
        "Unique no-show records after deduplication:",
        uniqueNoShows.length
      );

      // Calculate stats using deduplicated data
      const noShowCount = uniqueNoShows.length;

      // Count rebooked appointments (you may need to adjust this logic based on your data structure)
      // Assuming rebooked appointments have a specific status or field
      const rebookedCount = uniqueNoShows.filter(
        (record: any) =>
          record.rebooked === true || record.status === "REBOOKED"
      ).length;

      // Calculate recovery rate
      const recoveryRate =
        noShowCount > 0
          ? ((rebookedCount / noShowCount) * 100).toFixed(0) + "%"
          : "0%";

      // For total appointments, we might need to fetch all appointments
      // For now, using noShowCount as base
      const totalAppointments = noShowCount;

      setStats({
        totalAppointments,
        noShowCount,
        rebookedCount,
        recoveryRate,
      });
    } catch (error) {
      console.error("Error fetching no-show stats:", error);
      if (axios.isAxiosError(error)) {
        console.error("Axios error details:", {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
        });
      }
      toast.error("Failed to load no-show statistics.");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchNoShowStats();
  }, [fetchNoShowStats]);

  const handleRefresh = () => {
    fetchNoShowStats();
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
          padding: "24px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", padding: "24px", backgroundColor: "#FAFAFA" }}>
      {/* Header Section */}
      <Box sx={{ marginBottom: "24px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "8px",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 700,
                color: "#2D3748",
                marginBottom: "4px",
                fontFamily: "Quicksand, sans-serif",
              }}
            >
              No-Show
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                color: "#718096",
                fontWeight: 400,
                fontFamily: "Quicksand, sans-serif",
              }}
            >
              Send empathetic messages to rebook no-shows and cancellations.
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <IconButton
              onClick={handleRefresh}
              aria-label="Refresh statistics"
              sx={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #E2E8F0",
                borderRadius: "8px",
                padding: "8px",
                "&:hover": {
                  borderColor: "#CBD5E0",
                  backgroundColor: "#F7FAFC",
                },
              }}
            >
              <RefreshIcon width={20} height={20} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      {/* Stats Cards Section */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: "16px",
          marginBottom: "32px",
        }}
      >
        <StatsCard
          count={stats.totalAppointments}
          label="Total appointments"
          icon={<NoshowIcon width={24} height={24} color="#718096" />}
          color="#4FD1C5"
          trend="↑ 12%"
          trendPeriod="VS. last 90 days"
        />
        <StatsCard
          count={stats.noShowCount}
          label="No show"
          icon={<NoPatientIcon width={24} height={24} color="#718096" />}
          color="#F56565"
          trend="↑ 8%"
          trendPeriod="VS. last 90 days"
        />

        <StatsCard
          count={stats.rebookedCount}
          label="Successfully Rebooked"
          icon={<MedicIcon width={24} height={24} color="#718096" />}
          color="#48BB78"
          trend="↑ 15%"
          trendPeriod="VS. last 30 days"
        />

        <StatsCard
          count={stats.recoveryRate}
          label="Recovery Rate"
          icon={<PercentageIcon width={24} height={24} color="#718096" />}
          color="#ED8936"
          trend="↑ 7%"
          trendPeriod="VS. last 30 days"
        />
      </Box>

      {/* Table Section */}
      <Box sx={{ width: "100%" }}>
        <Outlet />
      </Box>
    </Box>
  );
};
